import { z } from "zod";
import { zql } from "../schema";
import {
  applyBuiltMessage,
  assertClientMaySetKind,
  buildEditPatch,
  buildMessage,
  type BuiltMessage,
  type MutateSurface,
} from "../message-builders";
import { deriveMessageKind } from "../message-kind-derivation";
import { messageKindSchema } from "../message-payload";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import { generateId } from "./helpers";
import {
  attachmentInsertSchema,
  insertAttachmentsForMessage,
  type MutatorCtx,
} from "./messages-cores";

// Message send / reply / edit + their atomic attachment variants. Schemas live
// with their cores so the contract + implementation move together. The thin
// `actions.*` mutator wrappers (actions.ts) and the server-mutator extension
// (server/server-mutators.ts) both call these cores.

export const actionSendMessageSchema = z.object({
  channelId: z.string(),
  userId: z.string(),
  content: z.string(),
  kind: messageKindSchema.optional(),
  payload: z.any().optional().nullable(),
  mentions: z.array(z.string()).optional(),
  messageId: z.string().optional(),
});

export const actionReplyToThreadSchema = z.object({
  threadId: z.string(),
  channelId: z.string(),
  userId: z.string(),
  content: z.string(),
  kind: messageKindSchema.optional(),
  payload: z.any().optional().nullable(),
  mentions: z.array(z.string()).optional(),
  isBroadcast: z.boolean().optional(),
  replyId: z.string().optional(),
});

export const actionEditMessageSchema = z.object({
  messageId: z.string(),
  content: z.string().optional(),
  payload: z.any().optional().nullable(),
  mentions: z.array(z.string()).optional(),
});

// Phase 7b PR D1 — atomic variants of `sendMessage` / `replyToThread`
// for the iOS path, which dispatches via the actions-family mutators
// (see `ZeroMutatorCatalog.actionsSendMessage` /
// `actionsReplyToThread` in `apps/native/ios/Ano/Core/Zero/`). The
// embedded message schema stays opaque-payload — `gifPayloadSchema`
// and other strict per-kind validators run inside `applyBuiltMessage`
// via `validatePayload`, which is the same path the non-atomic variant
// already takes.
export const actionSendMessageWithAttachmentsSchema =
  actionSendMessageSchema.extend({
    attachments: z.array(attachmentInsertSchema).min(1),
  });

export const actionReplyToThreadWithAttachmentsSchema =
  actionReplyToThreadSchema.extend({
    attachments: z.array(attachmentInsertSchema).min(1),
  });

export async function actionSendMessageCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof actionSendMessageSchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage } | null> {
  if (ctx?.sub && args.userId !== ctx.sub) {
    throw new Error("Unauthorized: userId mismatch");
  }
  if (tx.location === "server") {
    // Spaces (channels.type='space', post mig 120) are non-message
    // containers. Members ARE in channel_members on the space row,
    // so the membership check below passes — we need the type guard
    // here. Type check fires BEFORE membership so a space rejection
    // takes priority. Round-3 self-review fix: iOS uses this action
    // mutator exclusively for sends, and `applyBuiltMessage` calls
    // the table-level `tx.mutate.messages.insert` which bypasses
    // `messagesMutators.insert`'s type guard. Without this check the
    // DB-layer `reject_message_to_space` trigger raises, returning
    // a generic 500 with no friendly UI message.
    const channel = await tx.run(
      zql.channels.where("id", args.channelId).one(),
    );
    if (channel?.type === "space") {
      throw new Error("Unauthorized: spaces cannot receive messages");
    }
    const membership = await tx.run(
      zql.channel_members
        .where("channel_id", args.channelId)
        .where("user_id", ctx?.sub)
        .where("removed_at", "IS", null)
        .one(),
    );
    if (!membership) {
      throw new Error("Unauthorized: not a channel member");
    }
  }
  // Phase 7b — caller-wins fallback derive. When a caller (iOS, REST
  // /api/messages, etc.) supplies an explicit `kind`, leave it alone.
  // When `kind` is omitted (Chrome extension share-to-channel + any future
  // text-only server caller), derive from content. No attachments here:
  // text-only attachment-less paths are the only callers that omit kind.
  let kind = args.kind;
  let payload = args.payload ?? null;
  if (kind === undefined) {
    const derived = deriveMessageKind({
      content: args.content,
      attachments: [],
    });
    kind = derived.kind;
    if (payload == null) payload = derived.payload;
  }
  assertClientMaySetKind(kind, tx.location);

  const messageId = args.messageId ?? generateId();
  const existingMessage = await tx.run(
    zql.messages.where("id", messageId).one(),
  );
  if (existingMessage) {
    const isRetryOfSameMessage =
      existingMessage.channel_id === args.channelId &&
      existingMessage.user_id === args.userId &&
      existingMessage.content === args.content;
    if (isRetryOfSameMessage) {
      return null;
    }
    throw new Error(`Message ID conflict: ${messageId}`);
  }

  // Fetch sender's display_name and avatar_url for denormalization
  const sender = await tx.run(zql.users.where("id", args.userId).one());

  const now = Date.now();
  const built = buildMessage({
    id: messageId,
    channel_id: args.channelId,
    user_id: args.userId,
    content: args.content,
    kind,
    payload,
    mentions: args.mentions ?? null,
    sender_display_name: sender?.display_name ?? null,
    sender_avatar_url: sender?.avatar_url ?? null,
    created_at: now,
    updated_at: now,
    reply_count: 0,
    is_broadcast: false,
    is_edited: false,
  });
  await applyBuiltMessage(tx, built);
  return { built };
}

export async function actionReplyToThreadCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof actionReplyToThreadSchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage } | null> {
  if (ctx?.sub && args.userId !== ctx.sub) {
    throw new Error("Unauthorized: userId mismatch");
  }
  if (tx.location === "server") {
    // Derive channel from parent — don't trust caller-supplied channelId
    const parent = await tx.run(zql.messages.where("id", args.threadId).one());
    if (!parent) throw new Error("Thread parent not found");
    if (parent.channel_id !== args.channelId) {
      throw new Error("Unauthorized: channelId mismatch with thread parent");
    }
    // Spaces (channels.type='space', post mig 120) are non-message
    // containers. The parent message wouldn't exist on a space (the
    // BEFORE INSERT trigger blocks original sends), but defense-in-
    // depth: reject before fanning out. Round-3 self-review fix.
    const channel = await tx.run(
      zql.channels.where("id", parent.channel_id).one(),
    );
    if (channel?.type === "space") {
      throw new Error("Unauthorized: spaces cannot receive messages");
    }
    const membership = await tx.run(
      zql.channel_members
        .where("channel_id", parent.channel_id)
        .where("user_id", ctx?.sub)
        .where("removed_at", "IS", null)
        .one(),
    );
    if (!membership) {
      throw new Error("Unauthorized: not a channel member");
    }
  }
  // Phase 7b — caller-wins fallback derive (mirror of actionSendMessageCore).
  let kind = args.kind;
  let payload = args.payload ?? null;
  if (kind === undefined) {
    const derived = deriveMessageKind({
      content: args.content,
      attachments: [],
    });
    kind = derived.kind;
    if (payload == null) payload = derived.payload;
  }
  assertClientMaySetKind(kind, tx.location);

  const now = Date.now();

  // Fetch sender's display_name and avatar_url for denormalization
  const sender = await tx.run(zql.users.where("id", args.userId).one());

  // Build + insert the reply through the builder
  const built = buildMessage({
    id: args.replyId ?? generateId(),
    channel_id: args.channelId,
    user_id: args.userId,
    content: args.content,
    kind,
    payload,
    thread_id: args.threadId,
    mentions: args.mentions ?? null,
    sender_display_name: sender?.display_name ?? null,
    sender_avatar_url: sender?.avatar_url ?? null,
    created_at: now,
    updated_at: now,
    reply_count: 0,
    is_broadcast: args.isBroadcast ?? false,
    is_edited: false,
  });
  await applyBuiltMessage(tx, built);

  const parent = await tx.run(zql.messages.where("id", args.threadId).one());
  if (!parent) {
    return { built };
  }

  const existingReplyUsers = (parent.reply_users as string[] | null) ?? [];
  const nextReplyUsers = existingReplyUsers.includes(args.userId)
    ? existingReplyUsers
    : [...existingReplyUsers, args.userId].slice(-5);

  await tx.mutate.messages.update({
    id: parent.id,
    reply_count: (parent.reply_count ?? 0) + 1,
    reply_users: nextReplyUsers,
    latest_reply_at: now,
  });

  return { built };
}

export async function actionEditMessageCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof actionEditMessageSchema>;
  ctx: MutatorCtx;
}): Promise<{ updated: boolean }> {
  const existing = await tx.run(zql.messages.where("id", args.messageId).one());
  if (tx.location === "server") {
    if (!existing) return { updated: false };
    if (existing.user_id !== ctx?.sub) {
      throw new Error("Unauthorized: can only edit own messages");
    }
  }

  const now = Date.now();
  if (existing && (args.content !== undefined || args.payload !== undefined)) {
    const patch = buildEditPatch({
      id: args.messageId,
      existing: {
        kind: (existing.kind ?? "text") as Parameters<
          typeof buildEditPatch
        >[0]["existing"]["kind"],
        content: existing.content,
        payload: (existing.payload as Record<string, unknown> | null) ?? null,
      },
      changes: {
        ...(args.content !== undefined ? { content: args.content } : {}),
        ...(args.payload !== undefined ? { payload: args.payload } : {}),
      },
      updated_at: now,
    });
    // Cast at the Zero boundary — the builder-derived patch carries
    // strongly-typed fields that are semantically valid but don't line
    // up with Zero's generated row type (readonly + optional wraps).
    // Phase 7c-D: `mentions` JSONB column dropped (mig 127); junction
    // sync runs in the server-extension override.
    await tx.mutate.messages.update({
      ...patch,
    } as unknown as Parameters<typeof tx.mutate.messages.update>[0]);
  } else {
    // Optimistic fallback — thin update. On server this is reached when
    // `existing` is set but only mentions changed (no content/payload edit).
    // On client this is reached when `existing` is missing from cache.
    // Phase 7c-D: don't write `mentions` — column was dropped (mig 127).
    await tx.mutate.messages.update({
      id: args.messageId,
      ...(args.content !== undefined ? { content: args.content } : {}),
      is_edited: true,
      updated_at: now,
    });
  }

  // Post-update TOCTOU re-check is delegated to the
  // `actions.editMessage` server-mutator override
  // (`server/server-mutators.ts`). A concurrent `messages.delete` can
  // fire between the auth check above and `tx.mutate.messages.update`;
  // Zero's blind UPDATE WHERE pk silently no-ops, and the override
  // re-reads before calling `syncMessageMentionsJunction` so we don't
  // FK-fail on a vanished row.
  return { updated: true };
}

// ── Phase 7b PR D1: atomic message+attachments cores (actions family) ──
//
// iOS dispatches sends/replies via the actions-family mutators (see
// `ZeroMutatorCatalog.actionsSendMessage` /
// `actionsReplyToThread` in `apps/native/ios/Ano/Core/Zero/`). These
// wrappers mirror the messages-family wrappers in `messages-cores.ts`
// — they call the existing send/reply core for the message INSERT,
// then loop attachment INSERTs inside the same Zero mutator body so
// the whole bundle commits or rolls back atomically.
//
// Idempotent-retry contract: `actionSendMessageCore` returns `null`
// when the messageId already exists with matching channelId/userId/
// content (deduped retry of a previously-succeeded send). The wrapper
// honors that return — it does NOT re-INSERT the attachments, since
// they would PK-conflict with the rows the original send wrote.

export async function actionSendMessageWithAttachmentsCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof actionSendMessageWithAttachmentsSchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage } | null> {
  const { attachments, ...sendArgs } = args;
  const result = await actionSendMessageCore({ tx, args: sendArgs, ctx });
  if (result === null) return null;
  await insertAttachmentsForMessage(
    tx,
    attachments,
    result.built.message.id as string,
    ctx,
  );
  return result;
}

export async function actionReplyToThreadWithAttachmentsCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof actionReplyToThreadWithAttachmentsSchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage } | null> {
  const { attachments, ...replyArgs } = args;
  const result = await actionReplyToThreadCore({ tx, args: replyArgs, ctx });
  if (result === null) return null;
  await insertAttachmentsForMessage(
    tx,
    attachments,
    result.built.message.id as string,
    ctx,
  );
  return result;
}
